import Head from 'next/head';
import { useRouter } from 'next/router';
import { memo, useMemo } from 'react';
import { useHits, usePagination } from 'react-instantsearch';
import { Pages } from '~/lib';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { TPromotionCard } from '~/shared/components/PromotionCard/PromotionCard.definition';
import { convertCountryCodeToUppercase } from '~/shared/utils';
import { getRuntimeConfig } from '~/shared/utils/config';
import { usePage } from '~/templates/pages';
import { setSlashOnUrl } from '../Algoliga.utils';
import { AlgoliaHitType } from '../PLPModule.definitions';

type EntityReference = { '@id': string };

type BreadcrumbListItem = {
    '@type': 'ListItem';
    position: number;
    name: string;
    item: string;
};

type BreadcrumbListSchema = {
    '@type': 'BreadcrumbList';
    '@id': string;
    name: string;
    itemListElement: BreadcrumbListItem[];
};

type ProductListItem = {
    '@type': 'ListItem';
    position: number;
    name: string;
    url: string;
    item: EntityReference;
};

type OfferCatalogSchema = {
    '@type': 'OfferCatalog';
    '@id': string;
    name?: string;
    url: string;
    numberOfItems: number;
    itemListElement: ProductListItem[];
};

type CollectionPageSchema = {
    '@type': 'CollectionPage';
    '@id': string;
    url: string;
    name?: string;
    description?: string;
    inLanguage?: string;
    breadcrumb?: EntityReference;
    mainEntity?: EntityReference;
};

type PLPSchemaGraph = {
    '@context': 'https://schema.org';
    '@graph': (CollectionPageSchema | BreadcrumbListSchema | OfferCatalogSchema)[];
};

/**
 * `heroHeader` renders the PLP <h1> but is missing from the generated page
 * contract, so it has to be read through an explicit type (same reason
 * DynamicLayout/Standard reads it with a ts-ignore).
 */
type PLPPageData = Pages & { heroHeader?: string };

/**
 * Promotional content is mixed into the product hits, but is not a product.
 */
const isPromotionalContent = (hit: TProductDetails | TPromotionCard) =>
    'type' in hit && hit.type === AlgoliaHitType.PROMOTIONAL_CONTENT;

const getInLanguage = (culture?: string) =>
    culture?.includes('-') ? convertCountryCodeToUppercase(culture) : culture || undefined;

/**
 * JSON-LD for product list pages. Emits a single @graph so that CollectionPage,
 * BreadcrumbList and OfferCatalog can reference each other by @id.
 *
 * Must be rendered inside the PLP Algolia index context: the OfferCatalog mirrors
 * the hits - and their order - currently shown on the page.
 */
const PLPSchema = () => {
    const { breadcrumb, meta, culture, heroHeader } = usePage() as PLPPageData;
    const { asPath } = useRouter();
    const { config } = getRuntimeConfig();
    const { hits } = useHits<TProductDetails>();
    const { currentRefinement: currentPage } = usePagination();

    const pageUrl = useMemo(() => {
        const path = setSlashOnUrl(meta?.url || asPath.split('?')[0]);
        const pageQuery = currentPage > 0 ? `?page=${currentPage + 1}` : '';

        return `${config.NEXT_PUBLIC_URL}${path}${pageQuery}`;
    }, [meta?.url, asPath, currentPage, config.NEXT_PUBLIC_URL]);

    const innerHTML = useMemo<{ __html: string }>(() => {
        const collectionPageId = `${pageUrl}#collectionpage`;
        const breadcrumbId = `${pageUrl}#breadcrumb`;
        const offerCatalogId = `${pageUrl}#offercatalog`;

        const name = heroHeader || meta?.title;
        const description = meta?.description;
        const inLanguage = getInLanguage(culture);

        const breadcrumbList: BreadcrumbListItem[] =
            breadcrumb?.items?.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.title || '',
                item: config.NEXT_PUBLIC_URL + item.url,
            })) ?? [];

        const productList: ProductListItem[] = hits
            .filter((hit) => !isPromotionalContent(hit) && !!hit.url && !!hit.name)
            .map((hit, index) => {
                const productUrl = config.NEXT_PUBLIC_URL + setSlashOnUrl(hit.url);

                return {
                    '@type': 'ListItem',
                    position: index + 1,
                    name: hit.name,
                    url: productUrl,
                    item: { '@id': `${productUrl}#product` },
                };
            });

        const graph: PLPSchemaGraph['@graph'] = [
            {
                '@type': 'CollectionPage',
                '@id': collectionPageId,
                url: pageUrl,
                ...(name ? { name } : {}),
                ...(description ? { description } : {}),
                ...(inLanguage ? { inLanguage } : {}),
                ...(breadcrumbList.length ? { breadcrumb: { '@id': breadcrumbId } } : {}),
                ...(productList.length ? { mainEntity: { '@id': offerCatalogId } } : {}),
            },
        ];

        if (breadcrumbList.length) {
            graph.push({
                '@type': 'BreadcrumbList',
                '@id': breadcrumbId,
                name: 'Page Navigation',
                itemListElement: breadcrumbList,
            });
        }

        if (productList.length) {
            graph.push({
                '@type': 'OfferCatalog',
                '@id': offerCatalogId,
                ...(name ? { name } : {}),
                url: pageUrl,
                numberOfItems: productList.length,
                itemListElement: productList,
            });
        }

        const schemaObject: PLPSchemaGraph = {
            '@context': 'https://schema.org',
            '@graph': graph,
        };

        return { __html: JSON.stringify(schemaObject) };
    }, [
        breadcrumb,
        hits,
        pageUrl,
        heroHeader,
        meta?.title,
        meta?.description,
        culture,
        config.NEXT_PUBLIC_URL,
    ]);

    return (
        <Head>
            <script
                key="plp-schema"
                type="application/ld+json"
                dangerouslySetInnerHTML={innerHTML}
            />
        </Head>
    );
};

export default memo(PLPSchema);
